import {
  AppStatusSchema,
  capitalize,
  isDefined,
  messageFromError,
  toResponseError,
  type AppAddInput,
  type AppAddSuccess,
  type AppData,
  type AppGetInput,
  type AppGetSuccess,
  type AppIconRemoveInput,
  type AppIconRemoveSuccess,
  type AppId,
  type AppListInput,
  type AppListSuccess,
  type AppRemoveInput,
  type AppRemoveSuccess,
  type AppStatus,
  type AppUpdateInput,
  type AppUpdateSuccess,
} from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { BLACK_BLURHASH } from '../avatar'
import { type SelectItem } from '../types'
import { useTantalimQuery } from '../query'
import { useTantalimMutation } from '../mutation'
import {
  mutationAdd,
  mutationUpdate,
  type AddHookResponse,
  type GetHookResponse,
  type RemoveHookResponse,
  type UpdateHookResponse,
} from '../hooks'
import { connectAppKey, connectAppListKey } from './keys'

export const APP_STATUSES: AppStatus[] = AppStatusSchema.options

export const APP_STATUS_OPTIONS: SelectItem<AppStatus>[] = APP_STATUSES.map((status) => ({
  label: capitalize(status),
  value: status,
}))

export function useConnectApps(input?: AppListInput): GetHookResponse<AppData[]> {
  return useTantalimQuery<AppListInput, AppListSuccess, AppData[]>({
    url: '/connect/app/list',
    queryKey: connectAppListKey(input),
    input: input ?? {},
    convertResponse: ({ apps }) => apps,
  })
}

export function useConnectApp(appId?: AppId, enabled: boolean = true): GetHookResponse<AppGetSuccess> {
  const input: AppGetInput | undefined = isDefined(appId) ? { appId } : undefined

  return useTantalimQuery<AppGetInput | undefined, AppGetSuccess, AppGetSuccess>({
    url: '/connect/app/get',
    queryKey: connectAppKey(appId),
    input,
    convertResponse: (data) => data,
    options: {
      enabled: isDefined(appId) && enabled,
    },
  })
}

export function useAddConnectApp(): AddHookResponse<AppAddInput, AppData> {
  const mutation = useTantalimMutation<AppAddInput, AppAddSuccess>({
    url: '/connect/app/add',
  })
  const queryClient = useQueryClient()

  async function add(input: AppAddInput): Promise<AppData> {
    try {
      const added = await mutation.mutateAsync(input)
      await queryClient.invalidateQueries({ queryKey: connectAppListKey() })
      return added.app
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return mutationAdd(mutation, add)
}

type ConnectAppCache = {
  updateApp: (app: AppData) => Promise<void>
  updateIcon: (appId: AppId) => void
}

function useConnectAppCache(): ConnectAppCache {
  const queryClient = useQueryClient()

  async function updateApp(app: AppData): Promise<void> {
    queryClient.setQueryData<AppGetSuccess>(connectAppKey(app.appId), (old) => (isDefined(old) ? { ...old, app } : old))
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: connectAppListKey() }),
      queryClient.invalidateQueries({ queryKey: connectAppKey(app.appId) }),
    ])
  }

  function updateIcon(appId: AppId): void {
    const cacheBuster = Date.now()
    const update = <T extends Pick<AppData, 'appId' | 'avatarUrl' | 'avatarBlurhash'>>(app: T): T => ({
      ...app,
      avatarUrl: isDefined(app.avatarUrl) ? app.avatarUrl.split('?')[0] + '?v=' + cacheBuster : app.avatarUrl,
      avatarBlurhash: app.avatarBlurhash ?? BLACK_BLURHASH,
    })

    queryClient.setQueryData<AppGetSuccess>(connectAppKey(appId), (existing) => {
      if (!existing) return
      return { ...existing, app: update(existing.app) }
    })
    queryClient.setQueriesData<AppData[]>({ queryKey: connectAppListKey() }, (existing) => {
      if (!existing) return
      return existing.map((app) => (app.appId === appId ? update(app) : app))
    })
  }

  return { updateApp, updateIcon }
}

export function useConnectAppIconCache(): (appId: AppId) => void {
  return useConnectAppCache().updateIcon
}

export function useUpdateConnectApp(): UpdateHookResponse<AppUpdateInput, AppData> {
  const mutation = useTantalimMutation<AppUpdateInput, AppUpdateSuccess>({
    url: '/connect/app/update',
  })
  const { updateApp } = useConnectAppCache()

  async function update(input: AppUpdateInput): Promise<AppData> {
    try {
      const updated = await mutation.mutateAsync(input)
      await updateApp(updated.app)
      return updated.app
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return mutationUpdate(mutation, update)
}

export function useRemoveConnectApp(): RemoveHookResponse<AppRemoveInput> {
  const mutation = useTantalimMutation<AppRemoveInput, AppRemoveSuccess>({
    url: '/connect/app/remove',
  })
  const queryClient = useQueryClient()

  async function remove(input: AppRemoveInput): Promise<void> {
    try {
      await mutation.mutateAsync(input)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: connectAppListKey() }),
        queryClient.invalidateQueries({ queryKey: connectAppKey(input.appId) }),
      ])
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return {
    remove,
    isRemoving: mutation.isPending,
    error: messageFromError(mutation.error),
  }
}

export function useRemoveConnectAppIcon(): UpdateHookResponse<AppIconRemoveInput, AppData> {
  const mutation = useTantalimMutation<AppIconRemoveInput, AppIconRemoveSuccess>({
    url: '/connect/app/icon/remove',
  })
  const { updateApp } = useConnectAppCache()

  async function update(input: AppIconRemoveInput): Promise<AppData> {
    try {
      const updated = await mutation.mutateAsync(input)
      await updateApp(updated.app)
      return updated.app
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return mutationUpdate(mutation, update)
}
